const KEYBOARD_SEQUENCES = [
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  'poiuytrewq',
  'lkjhgfdsa',
  'mnbvcxz',
  'qazwsx',
  'wsxedc',
  'edcrfv',
  'rfvtgb',
];

const ALPHABET_SEQUENCES = [
  'abcdefghijklmnopqrstuvwxyz',
  'zyxwvutsrqponmlkjihgfedcba',
];

const NON_ANSWER_FRAGMENTS = [
  'dont know',
  "don't know",
  'do not know',
  'no idea',
  'no clue',
  'not sure',
  'you tell me',
];

const NON_ANSWER_EXACT = new Set([
  'na',
  'n a',
  'n/a',
  'nil',
  'none',
  'nothing',
  'test',
  'testing',
  'dummy',
  'sample',
  'placeholder',
  'asdf',
  'qwerty',
  'lorem ipsum',
]);

const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'can', 'company', 'data',
  'for', 'from', 'goal', 'have', 'in', 'into', 'is', 'it', 'its', 'no', 'of',
  'on', 'or', 'our', 'the', 'this', 'to', 'we', 'with',
]);

const ACRONYM_ALLOWLIST = new Set([
  'AI', 'API', 'B2B', 'B2C', 'CIBIL', 'CNC', 'CRM', 'ERP', 'FD', 'FDI', 'GST',
  'HR', 'IT', 'KPI', 'KYC', 'ML', 'MSME', 'NLP', 'NPA', 'OCR', 'POS', 'RBI',
  'ROI', 'SKU', 'TDS',
]);

const FIELD_PROFILES = {
  name: { minLetters: 2, minMeaningfulWords: 1 },
  short: { minLetters: 2, minMeaningfulWords: 1 },
  product: { minLetters: 6, minMeaningfulWords: 2 },
  profile: { minLetters: 12, minMeaningfulWords: 3 },
  goal: { minLetters: 8, minMeaningfulWords: 2 },
  challenge: { minLetters: 8, minMeaningfulWords: 2 },
  action: { minLetters: 6, minMeaningfulWords: 2 },
  table: { minLetters: 12, minMeaningfulWords: 3 },
  description: { minLetters: 8, minMeaningfulWords: 2 },
};

const normalizePhrase = (value = '') => (
  String(value).toLowerCase().replace(/[^a-z0-9/']+/g, ' ').replace(/\s+/g, ' ').trim()
);

const containsSequence = (lettersJoined, sequences, minLength = 4) => {
  for (const seq of sequences) {
    for (let len = minLength; len <= Math.min(6, seq.length); len += 1) {
      for (let start = 0; start <= seq.length - len; start += 1) {
        if (lettersJoined.includes(seq.slice(start, start + len))) return true;
      }
    }
  }
  return false;
};

const rawTokens = (text = '') => String(text).match(/[A-Za-z0-9]+/g) || [];

const isMeaningfulToken = (token) => {
  const upper = token.toUpperCase();
  const lower = token.toLowerCase();
  if (STOP_WORDS.has(lower) || NON_ANSWER_EXACT.has(lower)) return false;
  if (ACRONYM_ALLOWLIST.has(upper)) return true;
  if (/[A-Za-z]\d|\d[A-Za-z]/.test(token)) return true;
  return token.length >= 3;
};

const meaningfulTokenCount = (text = '') => rawTokens(text).filter(isMeaningfulToken).length;

const hasUnusualConsonantRun = (text = '') => {
  const vowels = new Set(['a', 'e', 'i', 'o', 'u']);
  let maxRun = 0;
  const words = String(text).match(/[A-Za-z]+/g) || [];
  for (const word of words) {
    const upper = word.toUpperCase();
    if (ACRONYM_ALLOWLIST.has(upper) || (word === upper && word.length <= 5)) continue;
    let run = 0;
    for (const char of word.toLowerCase()) {
      if (!vowels.has(char)) {
        run += 1;
        maxRun = Math.max(maxRun, run);
      } else {
        run = 0;
      }
    }
  }
  return maxRun >= 6;
};

const hasRepeatedCharacterPattern = (letters) => {
  if (letters.length <= 8) return false;
  const counts = new Map();
  letters.forEach((char) => counts.set(char, (counts.get(char) || 0) + 1));
  const topCount = Math.max(...counts.values());
  return topCount / letters.length > 0.4;
};

export function isGibberish(text, fieldType = 'description') {
  const cleaned = String(text || '').trim();
  if (!cleaned) return { bad: false, reason: '' };

  const profile = FIELD_PROFILES[fieldType] || FIELD_PROFILES.description;
  const normalised = normalizePhrase(cleaned);
  const compactNormalised = normalised.replace(/'/g, '');

  if (NON_ANSWER_EXACT.has(normalised) || NON_ANSWER_EXACT.has(compactNormalised)) {
    return { bad: true, reason: 'This looks like placeholder text. Please enter a real business description.' };
  }
  if (NON_ANSWER_FRAGMENTS.some(fragment => compactNormalised.includes(fragment))) {
    return { bad: true, reason: 'Please enter the actual business context instead of a non-answer.' };
  }

  const allLetters = Array.from(cleaned).filter(char => /\p{L}/u.test(char)).map(char => char.toLowerCase());
  if (allLetters.length < profile.minLetters) {
    return { bad: true, reason: 'This is too short to be meaningful for this field.' };
  }

  const asciiLetters = Array.from(cleaned).filter(char => /[A-Za-z]/.test(char)).map(char => char.toLowerCase());
  const asciiJoined = asciiLetters.join('');
  if (asciiJoined) {
    if (containsSequence(asciiJoined, KEYBOARD_SEQUENCES)) {
      return { bad: true, reason: 'This looks like random keyboard input rather than a business description.' };
    }
    if (!['name', 'short'].includes(fieldType) && containsSequence(asciiJoined, ALPHABET_SEQUENCES)) {
      return { bad: true, reason: 'This looks like an alphabet sequence rather than a business description.' };
    }
  }

  const latinRatio = asciiLetters.length / Math.max(allLetters.length, 1);
  if (asciiLetters.length >= 6 && latinRatio >= 0.6) {
    const vowelCount = asciiLetters.filter(char => 'aeiou'.includes(char)).length;
    const vowelRatio = vowelCount / asciiLetters.length;
    if (vowelRatio < 0.15 || vowelRatio > 0.75) {
      return { bad: true, reason: 'This does not look like normal business language. Please describe it in your own words.' };
    }
  }

  if (hasUnusualConsonantRun(cleaned)) {
    return { bad: true, reason: 'This contains an unusual run of consonants. Please describe it in your own words.' };
  }

  if (hasRepeatedCharacterPattern(allLetters)) {
    return { bad: true, reason: 'This looks like repeated characters rather than a real description.' };
  }

  if (latinRatio >= 0.4 && meaningfulTokenCount(cleaned) < profile.minMeaningfulWords) {
    return { bad: true, reason: 'This needs a little more real business detail.' };
  }

  return { bad: false, reason: '' };
}

export function firstCompanyGibberishError(company = {}) {
  const fields = [
    ['Company Name', 'Company name', 'name'],
    ['Industry Segment', 'Industry segment', 'short'],
    ['Customer Segment', 'Customer segment', 'short'],
    ['Company Profile', 'Company profile', 'profile'],
    ['Product/Services', 'Products / Services', 'product'],
  ];
  for (const [key, label, fieldType] of fields) {
    const result = isGibberish(company[key], fieldType);
    if (result.bad) return { field: key, text: `${label}: ${result.reason}` };
  }
  return null;
}

export function goalGibberishError(goal = {}, index = 0) {
  const goalResult = isGibberish(goal['Goal Description'], 'goal');
  if (goalResult.bad) return { field: 'Goal Description', text: `Goal ${index + 1} description: ${goalResult.reason}` };
  const challengeResult = isGibberish(goal.Challenges, 'challenge');
  if (challengeResult.bad) return { field: 'Challenges', text: `Goal ${index + 1} challenges: ${challengeResult.reason}` };
  return null;
}

export function firstGoalsGibberishError(goals = []) {
  for (let i = 0; i < goals.length; i += 1) {
    const result = goalGibberishError(goals[i], i);
    if (result) return result;
  }
  return null;
}

export function firstDataSourceGibberishError(dataSources = []) {
  for (let i = 0; i < dataSources.length; i += 1) {
    const result = isGibberish(dataSources[i]?.['Table Description'], 'table');
    if (result.bad) return { field: 'Table Description', text: `Data source ${i + 1} description: ${result.reason}` };
  }
  return null;
}

export function checkAssessmentForGibberish(form = {}) {
  return (
    firstCompanyGibberishError(form.company_information || {}) ||
    firstGoalsGibberishError(form.goals || []) ||
    firstDataSourceGibberishError(form.data_sources || [])
  );
}
